/**
 * Per-marketplace profit math, in minor units. Mirrors the item_ledger view in
 * the database, which is the source of truth for recorded sales; these
 * functions estimate before a sale exists.
 */
import { pctOf } from "./money.ts";

export interface FeeProfile {
  marketplace: string;
  commissionPct: number;
  fixedFeeMinor: number;
  paymentPct: number;
  paymentFixedMinor: number;
  lowPriceThresholdMinor: number | null;
  lowPriceFeeMinor: number | null;
  sellerPaysShipping: boolean;
  /** Extra % of the price on a sale through a boosted listing (Depop). */
  boostPct: number;
}

export interface CostBasis {
  purchasePriceMinor: number;
  packagingMinor: number;
  shippingMinor: number;
  buyerPaysShipping: boolean;
}

/** What the marketplace takes on a sale at `priceMinor`, payment processing included. */
export function marketplaceFee(priceMinor: number, p: FeeProfile, boosted = false): number {
  const selling =
    p.lowPriceThresholdMinor !== null && p.lowPriceFeeMinor !== null && priceMinor < p.lowPriceThresholdMinor
      ? p.lowPriceFeeMinor
      : pctOf(priceMinor, p.commissionPct) + p.fixedFeeMinor;
  const boost = boosted ? pctOf(priceMinor, p.boostPct) : 0;
  return selling + boost + pctOf(priceMinor, p.paymentPct) + p.paymentFixedMinor;
}

/** Take-home: price minus the marketplace's cut minus your outlay. */
export function estimateProfit(priceMinor: number, p: FeeProfile, cost: CostBasis): number {
  const sellerShips = p.sellerPaysShipping || !cost.buyerPaysShipping;
  const outlay = cost.purchasePriceMinor + cost.packagingMinor + (sellerShips ? cost.shippingMinor : 0);
  return priceMinor - marketplaceFee(priceMinor, p) - outlay;
}

/**
 * A low price that still clears `minProfitMinor`, or null if none under the
 * ceiling does. Fees are piecewise linear, so this bisects rather than solves.
 * Profit only dips at a low-price flat-fee threshold, and the bisection keeps
 * the invariant that `hi` clears the bar, so the answer always clears it even
 * where it is not the very lowest such price.
 */
export function floorPrice(p: FeeProfile, cost: CostBasis, minProfitMinor: number, ceilingMinor = 10_000_000): number | null {
  let lo = 0;
  let hi = ceilingMinor;
  if (estimateProfit(hi, p, cost) < minProfitMinor) return null;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (estimateProfit(mid, p, cost) >= minProfitMinor) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}
